import { ResultData } from './servicesCommon/apiTypes';
import { mapEnumToObj } from 'utils/utils';

export interface Entity {
	id: string | number | null;
	name: string;
}
export interface OptGroup {
	label: string;
	options: Entity[];
}

export type Nullable<T> = T | null | undefined;

export interface RetrievePassengersInfoRequest {
	bookingIds: number[];
}

export interface PassengerInfo {
	bookingId: number;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	phoneCodeCountryId: number;
	emailAddress: string;
	alternativeContactList: AlternativeContact[];
	journeyNotes: string | null;
	vip: boolean;
	referenceFields?: ReferenceField[];
}
export type RetrievePassengersInfoResponse = ResultData<PassengerInfo[]>;
export interface VisiblePassengerOptions {
	showVip?: boolean;
	showNotes?: boolean;
}

export interface AlternativeContact {
	firstName?: string;
	lastName?: string;
	fullName?: string;
	phoneNumber?: string;
	phoneCodeCountryId?: number;
	phoneCode?: string;
	emailAddress?: string;
	id?: string;
}

export type TypeOfInvoices = 'GS' | 'SP' | 'TR' | 'ST' | 'GSD' | 'SPD' | 'TRD';

export type StatusTable = 'download' | 'inProgress' | 'reGenerate' | undefined;

export enum WaitingTimeChargingType {
	freeMinutes = 1,
	gracePeriod = 2,
}

export enum WaitingTimeCategoryType {
	nonAirport,
	airport,
}

export enum DiscountType {
	base,
	baseAndWaitingTime,
}

// temp type, while we are using typescript 3.4
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export interface CountriesData {
	countryId: number;
	countryName: string;
	countryPhoneCode: string;
}

export interface SimpleFlight {
	id: number;
	flightNumber: string;
	airportAddress: string;
}

export interface GoogleAddress {
	placeId: string;
	fullAddress: string;
	name: string;
	flight?: SimpleFlight | null;
}

export interface FlightData {
	id: number;
	flightNumber: string;
	arrivalDate: string;
	arrivalPlaceId: string;
	arrivalAirportAddress: string;
	arrivalAddress: string;
	isFlightError: boolean;

	departureDate: string;
	departurePlaceId: string;
	departureAirportAddress: string;
	departureAddress: string;
}

export type GoogleAddressAndFlightData = GoogleAddress | FlightData;

export interface BookingAddress extends GoogleAddress {
	flight: {
		id: number;
		flightNumber: string;
		airportAddress: string;
	};
}

export interface BankDetails {
	bankName: string;
	accountName: string;
	accountNumber: string;
	sortCode: string;
	iban: string;
	bicSwift: string;
}

export interface PersonalUserDetails extends JourneyNotes, NotificationsByEmail {
	id: string;
	password: string;
	// userName: string;

	firstName: string;
	lastName: string;
	phoneNumber: string;
	phoneCodeCountryId: number;
	phoneCode: string;
	emailAddress: string;

	preferredCarTypeId: number;
	countryId: number;
	region: string;
	cityId: number;
	street: string;
	buildingNumber: string;
	postCode: string;

	formattedAddress: string;
	placeId: string;
	addressName: string;
	addressType: string;
	latitude: number;
	longitude: number;
	alternativeContactList: AlternativeContact[];
	creditCards: CreditCard[];

	referenceFields: ReferenceFieldWithValue[];
}

export interface DnDItem {
	rowInitialNumber: number;
}

export interface CreditCard {
	paymentMethodId: string;
	lastFourDigits: string;
	expirationMonth: string;
	expirationYear: string;
	brand: CCBrand;
	isExpired: boolean;
	isDeleted: boolean;
	isOneTimeCard: boolean;
}

export interface BookingCreditCard extends Omit<CreditCard, 'paymentMethodId'> {
	customerUserCreditCardId: number | string;
}

export enum CCBrand {
	americanExpress = 0,
	dinersClub = 1,
	discover = 2,
	jcb = 3,
	masterCard = 4,
	unionPay = 5,
	visa,
}

export type CreateStripeSessionResponse = ResultData<string>;

export interface GetCreditCardInfoRequest {
	paymentMethodId: string;
}

export type GetCreditCardInfoResponse = ResultData<BookingCreditCard>;

export enum CustomerPaymentMethod {
	corporate = 1,
	creditCard = 2,
}

export const WAITING_TIME_CHARGING_TYPE_MAP = mapEnumToObj(WaitingTimeChargingType);
export const WAITING_TIME_CATEGORY_TYPE_MAP = mapEnumToObj(WaitingTimeCategoryType, '-');

export enum BookingStatusEnum {
	unapproved = 2,
	amended = 3,
	cancelled = 5,
}

export interface BookingDetailsModal {
	bookingId: number;
	bookingNumber: string;
	pickupDateTime: string;
	carTypeName: string;
	customer: string;
	passengerId: string;
	bookingAddresses: string[];
	servicePartner: string;
	durationInMeters: number;
	durationInSeconds: number;
	customerCurrencySymbol: string;
	totalPriceInCustomerCurrency: number;
	numberOfPassengers: number;
	customerPaymentMethod: CustomerPaymentMethod;

	bookingStatus?: BookingStatusEnum;
	callSign?: string;
	declinedReason?: string;
}

export const BOOKING_STATUS_MAP = mapEnumToObj(BookingStatusEnum);

export interface AllBookingCreditCards {
	[bookingId: number]: BookingCreditCard;
}

export interface NotificationsByEmail {
	bookingConfirmation: boolean;
	invoiceConfirmation: boolean;
	calendarBookingEvent: boolean;
}

export interface VipStatus {
	vipStatus: boolean;
}

export interface JourneyNotes {
	journeyNotes: string | null
}

export type ReferenceFieldWithName = {
	id: number | string;
	name: string;
};

export interface ReferenceFieldWithValue {
	customerReferenceFieldId: number;
	defaultValue: string;
}

export interface ReferenceField {
	customerReferenceFieldId: number;
	referenceFieldName: string;
	value: string;
}

